import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.

// One-time session-id exchange page. Uses useRef (not useState) for the
// processed flag so we survive StrictMode double-invocation.
export default function AuthCallback() {
    const navigate = useNavigate();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        (async () => {
            // Parse session_id out of the URL fragment.
            const hash = window.location.hash || "";
            const match = hash.match(/session_id=([^&]+)/);
            if (!match) {
                navigate("/", { replace: true });
                return;
            }
            const sessionId = decodeURIComponent(match[1]);

            // On successful merge, drop any local anonymous draft into
            // the newly-created wedding so the user doesn't lose work.
            let localDraft = null;
            try {
                const raw = localStorage.getItem("wedding_draft_v2");
                if (raw) localDraft = JSON.parse(raw);
            } catch {
                // ignore
            }

            try {
                const res = await axios.post(
                    `${API}/auth/session`,
                    {},
                    {
                        headers: { "X-Session-ID": sessionId },
                        withCredentials: true,
                    },
                );

                // If it was a first-time login and we have a draft, push
                // it to the newly-created wedding via the authenticated
                // PUT endpoint. Non-fatal on failure.
                if (res.data?.is_new_user && localDraft) {
                    try {
                        await axios.put(
                            `${API}/admin/wedding`,
                            cleanDraft(localDraft),
                            { withCredentials: true },
                        );
                    } catch (err) {
                        console.warn("draft merge failed", err);
                    }
                }

                // Wipe the URL fragment and land on /editor.
                window.history.replaceState(
                    {},
                    "",
                    window.location.pathname,
                );
                navigate("/editor", { replace: true });
            } catch (err) {
                console.error("session exchange failed", err);
                navigate("/", {
                    replace: true,
                    state: { authError: true },
                });
            }
        })();
    }, [navigate]);

    return (
        <div className="h-[100dvh] w-full flex flex-col items-center justify-center bg-[var(--bg)] text-center px-6">
            <Loader2
                size={36}
                className="animate-spin text-[var(--ink-soft)] mb-4"
            />
            <h1 className="font-serif text-2xl">Iniciando sesión...</h1>
            <p className="text-sm text-[var(--ink-soft)] mt-1">
                Un momento, estamos preparando tu editor.
            </p>
        </div>
    );
}

// Mirror what the editor already sends on save so the shape matches
// backend expectations.
function cleanDraft(d) {
    const cleanVenue = (v) => ({
        name: v?.name || "",
        address: v?.address || "",
        map_url: v?.map_url || "",
        time: v?.time || "",
    });
    return {
        primary_name: d.primary_name || "",
        secondary_name: d.secondary_name || "",
        wedding_date: d.wedding_date || "",
        tagline: d.tagline || "",
        message: d.message || "",
        template: d.template || "classic-elegant",
        cover_image: d.cover_image || "",
        ceremony: cleanVenue(d.ceremony),
        party: cleanVenue(d.party),
        iban: d.iban || "",
        ibanEnabled: !!d.ibanEnabled,
        busEnabled: !!d.busEnabled,
        busDeparture: d.busDeparture || "",
        busReturn: d.busReturn || "",
        countdownEnabled: d.countdownEnabled ?? true,
        messageEnabled: d.messageEnabled ?? true,
    };
}
